import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Linkedin, Instagram, X } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import storilyIcon from '@/assets/storily-icon.png';
import storilyIconOffwhite from '@/assets/storily-icon-offwhite.png';
import ContactPopover from '@/components/footer/ContactPopover';
import { regionConfig, isUK } from '@/config/region';

const EditorialFooter: React.FC = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleNavClick = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = {
    product: [
      { label: 'Features', href: '/features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Demos', href: '/platform-demos' },
    ],
    company: [
      { label: 'About', href: '/about' },
      { label: 'Careers', href: '/careers' },
      { label: 'Contact', href: '/contact' },
    ],
    resources: [
      { label: 'Free Marketing', href: '/free-marketing' },
      { label: 'Help Center', href: '/help' },
      { label: 'Articles', href: '/blog' },
    ],
    services: [
      { label: 'Platform', href: '/features' },
      { label: 'Done-For-You', href: '/done-for-you' },
      { label: 'Agency Owners', href: '/agency-owners' },
    ],
    compare: [
      { label: 'Vs HubSpot', href: '/compare/hubspot' },
      { label: 'Vs Zoho', href: '/compare/zoho' },
      { label: 'Vs ClickFunnels', href: '/compare/clickfunnels' },
    ],
    legal: [
      { label: 'Privacy', href: '/privacy' },
      ...(isUK ? [{ label: 'GDPR', href: '/gdpr' }] : []),
      { label: 'Terms', href: '/terms' },
    ],
  };

  // Theme-aware colors
  const bgColor = theme === 'dark' ? '#111111' : '#f4f2f1';
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const hoverColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const borderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const socialColor = theme === 'dark' ? '#888888' : '#555555';
  const socialHoverColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const buttonBorderColor = theme === 'dark' ? '#444444' : '#CCCCCC';

  return (
    <footer 
      className="relative z-20 py-16 lg:py-20 transition-colors duration-300"
      style={{ backgroundColor: bgColor }}
    >
      <div className="max-w-[1152px] mx-auto px-8 lg:px-8">
        {/* Main Footer Content */}
        <div className="flex flex-wrap gap-8 lg:gap-0">
          {/* Logo & Description */}
          <div className="w-full md:w-auto md:flex-shrink-0 md:pr-12 lg:pr-16">
            <Link to="/" className="flex items-center gap-1 mb-4">
              <img 
                src={theme === 'dark' ? storilyIconOffwhite : storilyIcon} 
                alt="Storily" 
                width={28}
                height={28}
                className="h-7 w-auto"
              />
              <span className="font-semibold text-xl tracking-tight" style={{ color: headingColor }}>
                {regionConfig.siteName}
              </span>
            </Link>
            <p 
              className="text-sm leading-relaxed max-w-xs"
              style={{ color: textColor }}
            >
              The all-in-one platform<br />to manage & grow<br />your entire business.
            </p>
          </div>
          
          {/* Link Columns Container */}
          <div className="flex-1 grid grid-cols-2 max-sm:gap-x-8 max-sm:gap-y-6 sm:flex sm:flex-wrap md:flex-nowrap sm:justify-between sm:gap-6">
            {/* Product Links */}
            <div>
              <p className="font-semibold text-sm mb-4" style={{ color: headingColor }}>
                Product
              </p>
              <ul className="space-y-3">
                {footerLinks.product.map((link) => (
                  <li key={link.label}>
                    <button 
                      onClick={() => handleNavClick(link.href)}
                      className="text-sm transition-colors text-left cursor-pointer bg-transparent border-none p-0"
                      style={{ color: textColor }}
                      onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                      onMouseLeave={(e) => e.currentTarget.style.color = textColor}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Services Links */}
            <div>
              <p className="font-semibold text-sm mb-4" style={{ color: headingColor }}>
                Services
              </p>
              <ul className="space-y-3">
                {footerLinks.services.map((link) => (
                  <li key={link.label}>
                    <button 
                      onClick={() => handleNavClick(link.href)}
                      className="text-sm transition-colors text-left cursor-pointer bg-transparent border-none p-0"
                      style={{ color: textColor }}
                      onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                      onMouseLeave={(e) => e.currentTarget.style.color = textColor}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Compare Links */}
            <div>
              <p className="font-semibold text-sm mb-4" style={{ color: headingColor }}>
                Compare
              </p>
              <ul className="space-y-3">
                {footerLinks.compare.map((link) => (
                  <li key={link.label}>
                    <button 
                      onClick={() => handleNavClick(link.href)}
                      className="text-sm transition-colors text-left cursor-pointer bg-transparent border-none p-0"
                      style={{ color: textColor }}
                      onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                      onMouseLeave={(e) => e.currentTarget.style.color = textColor}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Resources Links */}
            <div>
              <p className="font-semibold text-sm mb-4" style={{ color: headingColor }}>
                Resources
              </p>
              <ul className="space-y-3">
                {footerLinks.resources.map((link) => (
                  <li key={link.label}>
                    <button 
                      onClick={() => handleNavClick(link.href)}
                      className="text-sm transition-colors text-left cursor-pointer bg-transparent border-none p-0"
                      style={{ color: textColor }}
                      onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                      onMouseLeave={(e) => e.currentTarget.style.color = textColor}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Company Links */}
            <div>
              <p className="font-semibold text-sm mb-4" style={{ color: headingColor }}>
                Company
              </p>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    {link.label === 'Contact' ? (
                      <ContactPopover>
                        <button 
                          className="text-sm transition-colors text-left cursor-pointer bg-transparent border-none p-0"
                          style={{ color: textColor }}
                          onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                          onMouseLeave={(e) => e.currentTarget.style.color = textColor}
                        >
                          {link.label}
                        </button>
                      </ContactPopover>
                    ) : (
                      <button 
                        onClick={() => handleNavClick(link.href)}
                        className="text-sm transition-colors text-left cursor-pointer bg-transparent border-none p-0"
                        style={{ color: textColor }}
                        onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                        onMouseLeave={(e) => e.currentTarget.style.color = textColor}
                      >
                        {link.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        {/* Social Icons and Buttons Row - Above divider */}
        <div className="flex flex-col sm:flex-row items-center sm:justify-between mt-6 sm:mt-12 mb-8 gap-6 sm:gap-0">
          {/* Social Icons */}
          <div className="flex items-center gap-3">
            <a 
              href="https://www.linkedin.com/company/story-marketing-group" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transition-colors"
              style={{ color: socialColor }}
              onMouseEnter={(e) => e.currentTarget.style.color = socialHoverColor}
              onMouseLeave={(e) => e.currentTarget.style.color = socialColor}
              aria-label="Visit our LinkedIn page"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a 
              href="https://www.instagram.com/storymarketinggroup/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transition-colors"
              style={{ color: socialColor }}
              onMouseEnter={(e) => e.currentTarget.style.color = socialHoverColor}
              onMouseLeave={(e) => e.currentTarget.style.color = socialColor}
              aria-label="Follow us on Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a 
              href="https://x.com/kyle_at_story" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transition-colors"
              style={{ color: socialColor }}
              onMouseEnter={(e) => e.currentTarget.style.color = socialHoverColor}
              onMouseLeave={(e) => e.currentTarget.style.color = socialColor}
              aria-label="Follow us on X"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a 
              href="https://www.tiktok.com/@storymarketinggroup" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transition-colors"
              style={{ color: socialColor }}
              onMouseEnter={(e) => e.currentTarget.style.color = socialHoverColor}
              onMouseLeave={(e) => e.currentTarget.style.color = socialColor}
              aria-label="Follow us on TikTok"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            <a 
              href="https://www.facebook.com/profile.php?id=61566263060491" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transition-colors"
              style={{ color: socialColor }}
              onMouseEnter={(e) => e.currentTarget.style.color = socialHoverColor}
              onMouseLeave={(e) => e.currentTarget.style.color = socialColor}
              aria-label="Follow us on Facebook"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a 
              href="https://www.youtube.com/@storymarketinggroupllc" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transition-colors"
              style={{ color: socialColor }}
              onMouseEnter={(e) => e.currentTarget.style.color = socialHoverColor}
              onMouseLeave={(e) => e.currentTarget.style.color = socialColor}
              aria-label="Subscribe to our YouTube channel"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>
          
          {/* Buttons - Stack vertically on mobile */}
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <button 
              onClick={() => setShowVideoModal(true)}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 rounded-lg hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
              style={{
                backgroundColor: theme === 'dark' ? '#1A1A1A' : 'white',
                color: theme === 'dark' ? '#FFFFFF' : 'black',
                border: `1px solid ${buttonBorderColor}`
              }}
            >
              <Play className="w-3 h-3" fill="currentColor" />
              Watch Demo
            </button>
            <button 
              onClick={() => navigate('/pricing#pricing-section')}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 rounded-lg hover:scale-[1.02] hover:shadow-md active:scale-[0.98] hover:opacity-90"
              style={{
                backgroundColor: theme === 'dark' ? '#FFFFFF' : 'black',
                color: theme === 'dark' ? '#000000' : 'white'
              }}
            >
              Start for free →
            </button>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div 
          className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ borderTop: `1px solid ${borderColor}` }}
        >
          {/* Left - Copyright */}
          <p className="text-sm text-center sm:text-left" style={{ color: textColor }}>
            © {new Date().getFullYear()} Story Marketing Group, LLC
            <span className="hidden sm:inline"> • </span>
            <br className="sm:hidden" />
            <span className="font-semibold">Built for Business Owners & Agencies</span>
          </p>
          
          {/* Right - Legal Links */}
          <div className="flex items-center justify-center sm:justify-start gap-3">
            {footerLinks.legal.map((link, index) => (
              <React.Fragment key={link.label}>
                {index > 0 && <span style={{ color: theme === 'dark' ? '#444444' : '#CCCCCC' }}>•</span>}
                <button 
                  onClick={() => handleNavClick(link.href)}
                  className="text-sm transition-colors cursor-pointer bg-transparent border-none p-0"
                  style={{ color: textColor }}
                  onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                  onMouseLeave={(e) => e.currentTarget.style.color = textColor}
                >
                  {link.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="relative w-full max-w-4xl mx-4 bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Video */}
            <video
              autoPlay
              controls
              className="w-full aspect-video"
              src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Explainer%20V6%20-%20Complete%20Overview%20Explainer.mp4"
            />
          </div>
        </div>
      )}
    </footer>
  );
};

export default EditorialFooter;
